import React from 'react';
import {
  BERRY_AI_INTEGRATIONS_LS_KEY,
  loadConnectedAiApiKeyIntegrationsFromBrowser,
} from 'utils/ai-integrations-browser-storage';
import { getCompanyAiStatus } from 'api/company-ai';

const GEMINI_ID = 'google_gemini_integration';

export const GEMINI_DEFAULT_MODEL_FALLBACK = 'gemini-2.5-flash';

export type GeminiConnectionState = {
  /** מפתח ב-localStorage של הדפדפן הנוכחי — היחיד שאפשר לקרוא איתו ל-Gemini מהדפדפן */
  connected: boolean;
  apiKey: string;
  defaultModel: string;
  /**
   * לחברה יש מנוע AI שמור בשרת (CompanyAiKeys). אין דרך לקרוא איתו ישירות
   * מהדפדפן — המפתח לא יוצא מהשרת — אבל אפשר לפנות דרך הנוד
   * (`utils/ask-ai-with-company-fallback`). לכן פיצ'ר שעבר לנתיב הזה צריך
   * לבדוק `available` ולא `connected` (חקירה 5.11: כפתור «ניסוח חכם» היה
   * מושבת לכל נציג שלא שמר את המפתח בדפדפן שלו).
   */
  serverKeyAvailable: boolean;
  /** `connected || serverKeyAvailable` — האם הפיצ'ר זמין בכלל */
  available: boolean;
};

/**
 * נקרא פעם אחת לכל טעינת דף ומשותף לכל הקוראים — בלי זה כל צרכן של ההוק היה
 * יורה בקשה משלו. כישלון נחשב «אין מפתח בשרת» ולא חוסם את המסלול המקומי.
 */
let serverKeyPromise: Promise<boolean> | null = null;
let serverKeyCached = false;

function fetchServerKeyAvailable(): Promise<boolean> {
  if (!serverKeyPromise) {
    serverKeyPromise = getCompanyAiStatus()
      .then((res) => {
        serverKeyCached = res.ok && res.status.status === 'ready' && Boolean(res.status.provider);
        return serverKeyCached;
      })
      .catch(() => false);
  }
  return serverKeyPromise;
}

function readGemini(serverKeyAvailable = serverKeyCached): GeminiConnectionState {
  const rows = loadConnectedAiApiKeyIntegrationsFromBrowser();
  const r = rows.find((x) => x.id === GEMINI_ID);
  const key = r?.config?.apiKey?.trim() ?? '';
  const connected = Boolean(key);
  return {
    connected,
    apiKey: key,
    defaultModel: r?.config?.defaultModel?.trim() || GEMINI_DEFAULT_MODEL_FALLBACK,
    serverKeyAvailable,
    available: connected || serverKeyAvailable,
  };
}

/** Imperative read (e.g. when opening a menu). */
export function getGeminiConnectionSnapshot(): GeminiConnectionState {
  return readGemini();
}

/**
 * Current Google Gemini API key + default model from Integrations (browser storage).
 */
export function useGeminiConnection(): GeminiConnectionState {
  const [state, setState] = React.useState<GeminiConnectionState>(() => readGemini());

  React.useEffect(() => {
    let cancelled = false;
    void fetchServerKeyAvailable().then((hasServerKey) => {
      if (!cancelled) setState(readGemini(hasServerKey));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const sync = () => setState(readGemini());
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === BERRY_AI_INTEGRATIONS_LS_KEY) sync();
    };
    window.addEventListener('storage', onStorage);
    /** Same-tab saves use localStorage without firing `storage`; refresh when the window regains focus. */
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', sync);
    };
  }, []);

  return state;
}
